/**
 * Sync-now STATE MACHINE. The backend returns ONE envelope for every outcome (ok / deduped /
 * blocked_quota / blocked_throttle / blocked_daily_sync / error); this hook turns it into a discriminated
 * machine state the UI renders explicitly — so a block is NEVER silently swallowed and `live_at` is
 * NEVER surfaced on a non-ok outcome.
 *
 * Invariants enforced here (not just by convention):
 *  - `liveAt` is exposed ONLY for the `ok` state (read from the envelope's `live_at`); every other
 *    state forces it to null even if a (malformed) server were to send one.
 *  - every settled state carries the quota snapshot when present, so a block can render it LOUD.
 *  - a successful sync calls onOk so the caller can invalidate + refetch the mirror slice.
 *
 * The mutation is keyed per (surface, account) so two buttons on the page don't clobber each other.
 */

import { useCallback, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import type { ApiError, QuotaSnapshot, SyncNowEnvelope, SyncNowStatus } from "@/api/types";
import { ApiRequestError } from "@/api/client";

export type SyncNowMachine =
  | { phase: "idle" }
  | { phase: "loading" }
  | {
      phase: "ok";
      /** Live fetch instant — ONLY ever set here. The mirror badge must NOT read this. */
      liveAt: string | null;
      quota: QuotaSnapshot | null;
      apiCallsSpent: number;
    }
  | { phase: "deduped"; quota: QuotaSnapshot | null }
  | {
      phase: "blocked";
      status: Extract<SyncNowStatus, "blocked_quota" | "blocked_throttle" | "blocked_daily_sync">;
      reason: string | null;
      quota: QuotaSnapshot | null;
    }
  | { phase: "error"; reason: string | null; quota: QuotaSnapshot | null; httpError?: ApiError };

function envelopeToState(env: SyncNowEnvelope): SyncNowMachine {
  switch (env.status) {
    case "ok":
      // live_at is meaningful ONLY here — read it solely from the ok branch.
      return {
        phase: "ok",
        liveAt: env.live_at,
        quota: env.quota,
        apiCallsSpent: env.api_calls_spent,
      };
    case "deduped":
      return { phase: "deduped", quota: env.quota };
    case "blocked_quota":
    case "blocked_throttle":
    case "blocked_daily_sync":
      return { phase: "blocked", status: env.status, reason: env.reason, quota: env.quota };
    case "error":
      return { phase: "error", reason: env.reason, quota: env.quota };
    default:
      // A body that had a `status` field but not a recognised outcome (a malformed/degraded envelope
      // that slipped past the client's status-only detection). Degrade to a LOUD error rather than an
      // undefined state — never silently swallow it.
      return { phase: "error", reason: null, quota: env.quota ?? null };
  }
}

export interface UseSyncNowResult {
  state: SyncNowMachine;
  /** True while a sync is in flight. */
  isPending: boolean;
  run: () => void;
  reset: () => void;
}

/**
 * @param fetcher the client call (returns the structured envelope; only throws on a non-envelope auth
 *                failure, which we surface as an error state rather than crashing the page).
 * @param onOk    called after a successful sync so the caller can invalidate the mirror cache.
 */
export function useSyncNow(
  fetcher: () => Promise<SyncNowEnvelope>,
  onOk?: () => void,
): UseSyncNowResult {
  const [state, setState] = useState<SyncNowMachine>({ phase: "idle" });

  const mutation = useMutation({
    mutationFn: fetcher,
    onMutate: () => setState({ phase: "loading" }),
    onSuccess: (env) => {
      const next = envelopeToState(env);
      setState(next);
      if (next.phase === "ok") onOk?.();
    },
    onError: (err) => {
      // A non-envelope failure (401/403/network). Still LOUD, never silent.
      const httpError = err instanceof ApiRequestError ? err.error : undefined;
      setState({
        phase: "error",
        reason: httpError?.code ?? null,
        quota: null,
        ...(httpError ? { httpError } : {}),
      });
    },
  });

  const run = useCallback(() => mutation.mutate(), [mutation]);
  const reset = useCallback(() => setState({ phase: "idle" }), []);

  return { state, isPending: mutation.isPending, run, reset };
}
